import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { components } from '@trackercop/contracts';
import { rootRoute } from './__root';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { LineageGraphView } from '@/components/lineage-graph';
import { LineageSidePanel } from '@/components/lineage-side-panel';
import type { LineageNode } from '@/lib/lineage-graph';

type DetectedScript = components['schemas']['DetectedScript'];

// API caps pageSize at 200 (see openapi.yaml). Pagination loop walks up to
// MAX_SCRIPT_PAGES * ALL_SCRIPTS_PAGE_SIZE records so the lineage graph
// reflects a full crawl; the truncation banner only fires above that.
const ALL_SCRIPTS_PAGE_SIZE = 200;
const MAX_SCRIPT_PAGES = 10;

export const crawlLineageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crawls/$crawlId/graph',
  component: LineagePage,
});

function LineagePage() {
  const { crawlId } = crawlLineageRoute.useParams();
  const [selectedNode, setSelectedNode] = useState<LineageNode | null>(null);
  const [beforeConsentOnly, setBeforeConsentOnly] = useState(false);

  const scriptsQuery = useQuery({
    queryKey: queryKeys.crawls.scriptsAll(crawlId, ALL_SCRIPTS_PAGE_SIZE),
    queryFn: async () => {
      const items: DetectedScript[] = [];
      let total = 0;
      for (let page = 1; page <= MAX_SCRIPT_PAGES; page++) {
        const { data, error } = await apiClient.GET('/crawls/{crawlId}/scripts', {
          params: {
            path: { crawlId },
            query: { page, pageSize: ALL_SCRIPTS_PAGE_SIZE },
          },
        });
        if (error) throw error;
        total = data.total;
        items.push(...data.items);
        if (items.length >= total || data.items.length < ALL_SCRIPTS_PAGE_SIZE) break;
      }
      return { items, total };
    },
  });

  const scripts = scriptsQuery.data?.items ?? [];
  const total = scriptsQuery.data?.total ?? 0;
  const underFetched = total > scripts.length;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link
            to="/crawls/$crawlId"
            params={{ crawlId }}
            className="text-xs text-muted-foreground hover:underline"
            data-testid="lineage-back-link"
          >
            ← Back to crawl
          </Link>
          <h1 className="text-xl font-semibold">Script lineage</h1>
          <p className="text-xs text-muted-foreground">
            Nodes are domains. An edge from A → B means a script on domain A loaded a script on domain B.
            Colour encodes the most severe category observed on the domain.
          </p>
        </div>
        <label className="flex items-center gap-1 text-xs">
          <input
            type="checkbox"
            checked={beforeConsentOnly}
            onChange={(e) => setBeforeConsentOnly(e.target.checked)}
            data-testid="lineage-before-consent-toggle"
          />
          Before-consent only
        </label>
      </div>
      {underFetched && (
        <div className="rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="lineage-truncated">
          Showing the first {scripts.length} of {total} scripts — graph is truncated.
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        {scriptsQuery.isLoading ? (
          <div className="rounded-md border bg-background p-6 text-sm text-muted-foreground">Loading scripts…</div>
        ) : (
          <LineageGraphView
            scripts={scripts}
            beforeConsentOnly={beforeConsentOnly}
            onSelectDomain={setSelectedNode}
          />
        )}
        <LineageSidePanel node={selectedNode} allScripts={scripts} />
      </div>
    </div>
  );
}
